import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { HttpErrorResponse, HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

export declare interface IErrorResponse {
  statusCode: number;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class RequestService {
  public isFetching$: BehaviorSubject<boolean>;
  private requestCount$: BehaviorSubject<number>;

  constructor(private http: HttpClient) {
    this.isFetching$ = new BehaviorSubject(false);

    this.requestCount$ = new BehaviorSubject(0);

    this.requestCount$.subscribe((count) => {
      if (count > 0 && !this.isFetching$.value) {
        this.isFetching$.next(true);
      } else if (count <= 0 && this.isFetching$.value) {
        this.isFetching$.next(false);
      }
    });
  }

  /**
   * Appends / at the end of the url, it not set
   */
  private joinUrl(url1: string, url2: string): string {
    if (
      url2.substring(0, 1) !== '/' &&
      url2.substring(url2.length - 1, 1) !== '/'
    ) {
      url2 = '/' + url2;
    }

    return url1 + url2;
  }

  public getApiUrl(url): string {
    return this.joinUrl(environment.apiUrl, url);
  }

  private handleError<T>(response): Observable<IErrorResponse> {
    if (response instanceof HttpErrorResponse) {
      console.error('Request error', response);
      return throwError(() =>
        response.status === 0
          ? {
              statusCode: 0,
              message: 'No connection to [' + response.url + ']',
            }
          : {
              statusCode: response.error?.statusCode ?? response.status,
              message: response.error?.message ?? response.statusText,
              error: response.error,
            }
      );
    }
    return throwError(() => ({ statusCode: 0, message: 'Unknown error' }));
  }

  private request<T>(
    url: string,
    method: string,
    body?: any,
    options?: any
  ): Observable<any> {
    const fullUrl = this.getApiUrl(url);
    setTimeout(() => this.requestCount$.next(this.requestCount$.value + 1));

    return this.http
      .request<T>(method, fullUrl, { ...options, body })
      .pipe(
        catchError(this.handleError),
        finalize(() => {
          this.requestCount$.next(
            Math.max(this.requestCount$.value - 1, 0)
          );
        })
      );
  }

  public get<T>(url: string, params?: any): Observable<T> {
    return this.request(url, 'GET', null, { params });
  }

  /**
   * GET request that does not affect the loading bar (request count).
   * Use for non-critical or slow endpoints (e.g. antibioticos) so the bar can hide when other requests finish.
   */
  public getNoLoading<T>(url: string, params?: any): Observable<T> {
    const fullUrl = this.getApiUrl(url);
    return this.http
      .request<T>('GET', fullUrl, { params })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  public post<T>(url: string, body?: T, options?: any): Observable<any> {
    return this.request(url, 'POST', body, options);
  }

  public put<T>(url: string, body: T): Observable<any> {
    return this.request(url, 'PUT', body);
  }

  public delete<T>(url: string, body?: T): Observable<any> {
    return this.request(url, 'DELETE', body);
  }
}
